// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a machine's health and activity (docs/konvo-agents/HEALTH.md): which checks are a
// problem and which one leads, activity filtered to problems and grouped by day, the account form's
// defaults and the request it sends, and the activity loads (the owner's own machines and, for
// staff, anyone's in Team insights). The words live in components/AgentHealthCopy.ts.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import type {
	AgentAccountDriver,
	AgentAccountRequest,
	AgentActivity,
	AgentActivityEvent,
	AgentConnection,
	AgentHealthCheck,
	AgentHealthState,
	AgentMachineHealth,
} from '@konvo/schema/src/domains/konvo/Agents';
import {makeAutoObservable, observable, runInAction} from 'mobx';

const RANK: Record<AgentHealthState, number> = {bad: 3, warn: 2, ok: 1, off: 0};

/** The checks that need a look (bad, then warn), in the order the machine lists them otherwise. */
export function problemChecks(health: AgentMachineHealth | null | undefined): Array<AgentHealthCheck> {
	return (health?.checks ?? [])
		.filter((check) => check.state === 'bad' || check.state === 'warn')
		.map((check, index) => ({check, index}))
		.sort((a, b) => RANK[b.check.state] - RANK[a.check.state] || a.index - b.index)
		.map(({check}) => check);
}

/** The check the collapsed line leads with, and whose fix it offers. */
export function mainProblem(health: AgentMachineHealth | null | undefined): AgentHealthCheck | null {
	return problemChecks(health)[0] ?? null;
}

/** The badge a machine shows: an offline machine is off, whatever its last checks said. */
export function machineState(health: AgentMachineHealth | null | undefined, online: boolean): AgentHealthState {
	if (!online) return 'off';
	return health?.state ?? 'ok';
}

export type ActivityFilter = 'all' | 'problems';

export function isProblemEvent(event: Pick<AgentActivityEvent, 'level'>): boolean {
	return event.level !== 'info';
}

export function filterActivity(
	events: ReadonlyArray<AgentActivityEvent>,
	filter: ActivityFilter,
): Array<AgentActivityEvent> {
	return filter === 'problems' ? events.filter(isProblemEvent) : [...events];
}

/** The local calendar day of a moment, "2026-10-27". */
export function dayKey(at: number): string {
	const date = new Date(at);
	const pad = (n: number) => String(n).padStart(2, '0');
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export interface ActivityDay {
	day: string;
	/** Midnight of the day, local time. */
	start: number;
	events: Array<AgentActivityEvent>;
}

/** Events (newest first) by local day, newest day first; each day keeps the events' order. */
export function groupByDay(events: ReadonlyArray<AgentActivityEvent>): Array<ActivityDay> {
	const days: Array<ActivityDay> = [];
	for (const event of events) {
		const at = Date.parse(event.lastAt ?? event.at);
		if (!Number.isFinite(at)) continue;
		const day = dayKey(at);
		let group = days.find((entry) => entry.day === day);
		if (!group) {
			group = {day, start: new Date(at).setHours(0, 0, 0, 0), events: []};
			days.push(group);
		}
		group.events.push(event);
	}
	return days.sort((a, b) => b.start - a.start);
}

/** "~/.claude-work-2" for a new Claude account named "Work 2"; "~/.codex-work-2" for Codex. */
export function defaultHomePath(driver: AgentAccountDriver, name: string): string {
	const slug = name
		.normalize('NFKD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 40);
	const base = driver === 'codex' ? '~/.codex' : '~/.claude';
	return slug ? `${base}-${slug}` : base;
}

/** A variable on the account form: one kept on the machine (value unknown here) or one typed here. */
export interface AccountEnvRow {
	name: string;
	/** Typed here; null on a kept one until it is replaced. */
	value: string | null;
	/** Already on the machine: its value stays there unless replaced or removed. */
	kept: boolean;
	removed: boolean;
}

export interface AccountForm {
	driver: AgentAccountDriver;
	name: string;
	color: string | null;
	homePath: string;
	binaryPath: string;
	env: Array<AccountEnvRow>;
}

export function formFromConnection(connection: AgentConnection): AccountForm {
	const config = connection.config;
	return {
		driver: connection.driver === 'codex' ? 'codex' : 'claudeAgent',
		name: config?.displayName ?? connection.providerName,
		color: config?.accentColor ?? null,
		homePath: config?.homePath ?? '',
		binaryPath: config?.binaryPath ?? '',
		env: (config?.environmentNames ?? []).map((name) => ({name, value: null, kept: true, removed: false})),
	};
}

export const ENV_NAME = /^[A-Za-z_][A-Za-z0-9_]{0,119}$/;

/**
 * What Save sends: a new account leaves the instance id (and an untouched default folder) to the
 * bridge; an edit only sends the variables that change (set or dropped), never the kept ones.
 */
export function accountRequest(
	form: AccountForm,
	existing: AgentConnection | null,
): Extract<AgentAccountRequest, {action: 'save'}> {
	const environment: Array<{name: string; value: string | null}> = [];
	for (const row of form.env) {
		const name = row.name.trim();
		if (!ENV_NAME.test(name)) continue;
		if (row.removed) {
			if (row.kept) environment.push({name, value: null});
			continue;
		}
		if (row.value != null && (row.value !== '' || !row.kept)) environment.push({name, value: row.value});
	}
	const home = form.homePath.trim();
	const binary = form.binaryPath.trim();
	const before = existing?.config;
	return {
		action: 'save',
		...(existing ? {instanceId: existing.provider} : {}),
		driver: form.driver,
		displayName: form.name.trim(),
		...(form.color ? {accentColor: form.color} : {}),
		...(existing ? {} : {enabled: true}),
		...(home && (existing ? home !== (before?.homePath ?? '') : true) ? {homePath: home} : {}),
		...(binary && binary !== (before?.binaryPath ?? '') ? {binaryPath: binary} : {}),
		...(environment.length ? {environment} : {}),
	};
}

/** Where an activity list comes from: the owner's machine, or anyone's for staff (read-only). */
export type ActivitySource = 'own' | 'team';

export interface ActivityLoad {
	data: AgentActivity | null;
	loading: boolean;
	failed: boolean;
}

const activityKey = (source: ActivitySource, machineId: string) => `${source}:${machineId}`;

export class AgentActivityStore {
	loads = observable.map<string, ActivityLoad>();

	constructor() {
		makeAutoObservable(this);
	}

	of(source: ActivitySource, machineId: string): ActivityLoad | null {
		return this.loads.get(activityKey(source, machineId)) ?? null;
	}

	/** Loads (or reloads, keeping what shows) a machine's activity. */
	async load(source: ActivitySource, machineId: string): Promise<void> {
		const key = activityKey(source, machineId);
		const before = this.loads.get(key);
		this.loads.set(key, {data: before?.data ?? null, loading: true, failed: false});
		try {
			const response = await http.get<AgentActivity>(
				source === 'team'
					? Endpoints.KONVO_AGENTS_TEAM_MACHINE_ACTIVITY(machineId)
					: Endpoints.KONVO_AGENTS_ACTIVITY(machineId),
			);
			runInAction(() => this.loads.set(key, {data: response.body, loading: false, failed: false}));
		} catch {
			runInAction(() => this.loads.set(key, {data: before?.data ?? null, loading: false, failed: true}));
		}
	}
}

export default new AgentActivityStore();
